import { ValidationError } from '../../../shared/exceptions/validation.error';
import { BaseService } from '../../base';
import type {
  AttachQuotePayload,
  BusinessUnderwritingMessage,
  BusinessUnderwritingMessagesResponse,
  CalculateQuotePayload,
  CalculateQuoteResponse,
  CreateQuotePayload,
  CreateQuoteResponse,
  DecideGlobalQuoteSubscriptionPayload,
  DecideGlobalQuoteSubscriptionResponse,
  ProposalDetails,
} from '../types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

export class CapitalGlobalQuotesService extends BaseService {
  constructor() {
    super();
  }

  async createQuote(payload: CreateQuotePayload): Promise<{
    success: boolean;
    data?: CreateQuoteResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = '/portal/v1/global/quotes';
      const response = await this.post(path, payload, PORTAL_API_URL);
      return await this.handleJsonResponse<CreateQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar cotação',
      };
    }
  }

  async updateQuote(
    id: string,
    payload: Partial<AttachQuotePayload>
  ): Promise<{
    success: boolean;
    data?: CreateQuotePayload;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/global/quotes/${id}/attach-element`;
      const response = await this.post(path, payload, PORTAL_API_URL);

      if (response.status === 422) {
        const data = (await response.json()) as {
          detail?: string;
          errors: Record<string, { code: string; message: string }>;
        };

        const normalizedFieldErrors = Object.fromEntries(
          Object.entries(data.errors ?? {}).map(([key, value]) => [
            key,
            {
              ...value,
              message: String(value?.message ?? '').trim(),
            },
          ])
        ) as Record<string, { code: string; message: string }>;

        const messages =
          Object.values(normalizedFieldErrors)
            ?.map(error => error.message)
            .filter(Boolean)
            .map(message => message.trim()) ?? [];

        const validationError = new ValidationError(messages);
        const rawDetail = String(data.detail ?? '').trim();
        const detailFromResponse =
          rawDetail &&
          rawDetail !== 'Validation error' &&
          rawDetail !== 'A proposta foi rejeitada pela validação'
            ? rawDetail
            : undefined;

        if (detailFromResponse) {
          validationError.detail = detailFromResponse;
        } else if (
          messages.length > 0 &&
          messages.every(m => m.includes(' - '))
        ) {
          const prefixes = messages.map(m => m.split(' - ')[0].trim());
          if (prefixes.every(p => p === prefixes[0])) {
            validationError.detail = prefixes[0];
            validationError.messages = messages.map(m =>
              m.split(' - ').slice(1).join(' - ').trim()
            );
          }
        } else if (rawDetail && messages.length > 0) {
          validationError.detail = rawDetail;
        }

        validationError.fieldErrors = normalizedFieldErrors;
        throw validationError;
      }

      return await this.handleJsonResponse<CreateQuotePayload>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;

      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao atualizar cotação',
      };
    }
  }

  async calculateQuote(payload: CalculateQuotePayload): Promise<{
    success: boolean;
    data?: CalculateQuoteResponse;
    error?: string;
    message?: string;
    underwritingMessages?: BusinessUnderwritingMessage[];
  }> {
    try {
      const path = `/portal/v1/global/quotes/${payload.policyId}/calculate`;
      const response = await this.post(path, {}, PORTAL_API_URL);

      if (response.status === 400) {
        const raw = await response.text();
        const body = raw
          ? ((): {
              detail?: string;
              message?: string;
              underwritingMessages?: BusinessUnderwritingMessage[];
            } | null => {
              try {
                return JSON.parse(raw);
              } catch {
                return null;
              }
            })()
          : null;

        if (body && Array.isArray(body.underwritingMessages)) {
          return {
            success: false,
            error:
              body.detail ||
              body.message ||
              'Cotação enviada para análise da subscrição',
            underwritingMessages: body.underwritingMessages,
          };
        }

        return {
          success: false,
          error:
            body?.detail ||
            body?.message ||
            `Erro ${response.status}: ${response.statusText}`,
        };
      }

      return await this.handleJsonResponse<CalculateQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao calcular cotação',
      };
    }
  }

  async decideSubscription(
    policyId: string,
    payload: DecideGlobalQuoteSubscriptionPayload,
  ): Promise<{
    success: boolean;
    data?: DecideGlobalQuoteSubscriptionResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/global/quotes/${encodeURIComponent(policyId)}/subscription/decision`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      return await this.handleJsonResponse<DecideGlobalQuoteSubscriptionResponse>(
        response,
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao decidir subscrição da cotação',
      };
    }
  }

  async calculateAnalysis(policyId: string): Promise<{
    success: boolean;
    data?: CalculateQuoteResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/global/quotes/${encodeURIComponent(policyId)}/calculate-analysis`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<CalculateQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao calcular cotação em análise',
      };
    }
  }

  async getUnderwritingMessages(policyId: string): Promise<{
    success: boolean;
    data?: BusinessUnderwritingMessagesResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/global/quotes/${encodeURIComponent(policyId)}/underwriting-messages`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<BusinessUnderwritingMessagesResponse>(
        response,
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar mensagens de subscrição',
      };
    }
  }

  async getProposalDetails(policyId: string): Promise<{
    success: boolean;
    data?: ProposalDetails;
    error?: string;
  }> {
    void policyId;
    throw new Error('not implemented');
  }
}

export const capitalGlobalQuotesService = new CapitalGlobalQuotesService();
